/**
 * Menu da conta da suíte (SPEC-v2 2.4, `SUI-13`) para o lançador, Automações, Estúdio, Equipe e
 * Conta: Conta, Segurança, Equipe (quem gerencia), Aparência e **Sair**. O Painel usa o menu dele
 * (com "Ver como" e densidade), com os mesmos itens da suíte.
 */
import { Contrast, KeyRound, LogOut, Moon, Sun, UserRound, Users } from 'lucide-react';
import { Suspense, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Avatar } from '@/components/Avatar';
import type { MenuItem } from '@/components/Menu';
import { menuPanel } from '@/components/Menu';
import { useSession } from '@/data/auth/session';
import { useThemePref } from '@/lib/theme';
import { ROLE_NAME, suitePageCopy } from './copy';
import { useSignOut } from './hooks';
import styles from './SuiteAccountMenu.module.css';

export function SuiteAccountMenu({ className }: { className?: string } = {}) {
  const session = useSession();
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const [, navigate] = useLocation();
  const [theme, setTheme] = useThemePref();
  const signOut = useSignOut();
  if (open && !seen) setSeen(true);
  useEffect(() => menuPanel.preloadWhenIdle(), []);
  if (!session) return null;
  const c = suitePageCopy.account;
  const v = session.viewer;
  const items: MenuItem[] = [
    { label: `${v.name} · ${ROLE_NAME[v.role]}`, onSelect: () => {}, disabled: true, group: 'me' },
    { label: c.profile, icon: UserRound, onSelect: () => navigate('~/conta/perfil'), group: 'conta' },
    { label: c.security, icon: KeyRound, onSelect: () => navigate('~/conta/seguranca'), group: 'conta' },
    ...(session.viewer.permissions.includes('users:manage')
      ? [{ label: c.team, icon: Users, onSelect: () => navigate('~/equipe/pessoas'), group: 'conta' }]
      : []),
    ...(['system', 'light', 'dark'] as const).map((t) => ({
      label: `${c.theme}: ${c.themes[t]}`,
      icon: t === 'light' ? Sun : t === 'dark' ? Moon : Contrast,
      checked: theme === t,
      onSelect: () => setTheme(t),
      group: 'theme',
    })),
    { label: c.signOut, icon: LogOut, onSelect: () => void signOut(), group: 'out' },
  ];
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={className ? `${styles.trigger} ${className}` : styles.trigger}
        aria-label={c.trigger(v.initials, v.name)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((o) => !o)}
        data-testid="account-menu"
      >
        <Avatar initials={v.initials} size={28} tone="accent" />
      </button>
      {(open || seen) && (
        <Suspense fallback={null}>
          <menuPanel.Part
            anchor={anchor}
            id={id}
            open={open}
            onClose={() => setOpen(false)}
            items={items}
            label={c.menu}
            align="end"
          />
        </Suspense>
      )}
    </>
  );
}
